import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { BridgeIframe } from '#src/components/offRamp/OffRampKycOnboarding/BridgeIframe/index.tsx';
import { OffRampStepLayout } from '#src/components/offRamp/OffRampKycOnboarding/OffRampStepLayout/index.tsx';
import { OFF_RAMP_ONBOARDING_PATH } from '#src/components/offRamp/OffRampKycOnboarding/type.ts';
import { useOffRampOnboardingState } from '#src/components/offRamp/OffRampKycOnboarding/useOffRampOnboardingState.tsx';
import { useStartOffRampBridgeKyc } from '#src/components/offRamp/OffRampKycOnboarding/useStartOffRampBridgeKyc.tsx';
import { ORIGIN } from '#src/config.ts';
import { glossary } from '#src/lib/glossary.ts';

import { toWidgetUrl } from './toWidgetUrl.ts';

const POLL_INTERVAL = 5000;

// Permissions Bridge requires for the embedded Persona KYC widget.
const KYC_IFRAME_SANDBOX =
  'allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-top-navigation-by-user-activation';

type Props = {
  onComplete: () => void;
  onClose?: () => void;
};

export const KycStep = ({ onComplete, onClose }: Props) => {
  const startKyc = useStartOffRampBridgeKyc();
  const { state } = useOffRampOnboardingState({ pollInterval: POLL_INTERVAL });

  const [kycUrl, setKycUrl] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onActive = useEffectEvent(onComplete);

  useEffect(() => {
    if (state.status === 'active') onActive();
  }, [state.status]);

  const start = useEffectEvent(async () => {
    setError(null);
    const result = await startKyc(`${ORIGIN}${OFF_RAMP_ONBOARDING_PATH}`);

    const payload = result.data?.startOffRampBridgeKyc;
    if (payload?.errors?.length) {
      setError(payload.errors[0].message);
      return;
    }
    if (payload?.kycLinkUrl) setKycUrl(toWidgetUrl(payload.kycLinkUrl, ORIGIN));
  });

  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || submitted) return;
    if (
      state.status === 'loading' ||
      state.status === 'active' ||
      state.status === 'terminalFailure'
    ) {
      return;
    }
    opened.current = true;
    start();
  }, [state.status, submitted]);

  const onCloseIframe = () => {
    setKycUrl(null);
    setSubmitted(true);
  };

  if (state.status === 'terminalFailure') {
    return (
      <OffRampStepLayout
        title={<FormattedMessage defaultMessage="Verification unsuccessful" />}
        description={
          <FormattedMessage defaultMessage="We couldn't verify your identity. Please contact support if you think this is a mistake." />
        }
      />
    );
  }

  if (submitted) {
    return (
      <OffRampStepLayout
        title={<FormattedMessage defaultMessage="Verification in progress" />}
        description={
          <FormattedMessage defaultMessage="Thanks — Bridge is reviewing your details. This usually takes a few minutes but can take longer. We'll update your status once it's done — you can close this and come back later." />
        }
      >
        {!!onClose && (
          <Button size="medium" fullWidth onClick={onClose}>
            <FormattedMessage {...glossary.close} />
          </Button>
        )}
      </OffRampStepLayout>
    );
  }

  if (kycUrl) {
    return (
      <BridgeIframe
        url={kycUrl}
        onMessage={onCloseIframe}
        onClose={onCloseIframe}
        sandbox={KYC_IFRAME_SANDBOX}
      />
    );
  }

  if (error) {
    return (
      <OffRampStepLayout
        title={<FormattedMessage defaultMessage="Verify your identity" />}
      >
        <Vertical gap={2}>
          <BodyM color="var(--c-red-400)">{error}</BodyM>
          <Button size="medium" fullWidth onClick={start}>
            <FormattedMessage defaultMessage="Try again" />
          </Button>
        </Vertical>
      </OffRampStepLayout>
    );
  }

  return <LoadingIndicator fullHeight />;
};
